/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IProductDimensionsWithAvailabilitiesFull } from '@msdyn365-commerce/commerce-entities';
import msdyn365Commerce, { getUrlSync, ICoreContext } from '@msdyn365-commerce/core-internal';
import {
    Customer,
    InventoryAvailabilitySearchCriteria,
    ProductDimensionValueInventoryAvailability,
    ProductInventoryAvailability,
    ProductsDataActions
} from '@msdyn365-commerce/retail-proxy';
import {
    ArrayExtensions,
    DeliveryMode,
    Dictionary,
    getInventoryLevelCodeFromDimensionValue,
    getInventoryLevelCodeFromProductAvailability,
    IDimensionsApp,
    IDimensionsConfig,
    InventoryLevels,
    InventoryLevelValues,
    ObjectExtensions,
    OrderTemplate
} from '@msdyn365-commerce-modules/retail-actions';
import {
    Button,
    Collapse,
    ILabeledDropdownOnChangeNotification,
    isMobile,
    LabeledDropdown,
    Quantity,
    VariantType
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { AddToCartFunctionalComponent, IAddToCartResources } from '../add-to-cart';
import { AddProductsToOrderTemplateComponent, IAddProductsToOrderTemplateResources } from '../order-template';
import {
    getDimensionName,
    IProductDimensionsMatrixDimensionSelectionResources,
    IProductDimensionsMatrixEnterAllQuantitiesResources,
    ProductDimensionsMatrixDimensionItemComponent,
    ProductDimensionsMatrixDimensionSelectionComponent,
    ProductDimensionsMatrixEnterAllQuantitiesComponent
} from './components';

/**
 * Resources for the product dimensions matrix component.
 */
export interface IProductDimensionsMatrixResources
    extends IProductDimensionsMatrixEnterAllQuantitiesResources,
        IProductDimensionsMatrixDimensionSelectionResources,
        IAddProductsToOrderTemplateResources,
        IAddToCartResources {
    productDimensionsMatrixInputQuantityAriaLabel: string;
    productDimensionsMatrixTotalItemsLabel: string;
    productDimensionsMatrixOutOfStockLabel: string;
    productDimensionsMatrixClearAllText: string;
    productDimensionsMatrixAddItemsToCartButtonText: string;

    productDimensionsMatrixDisplayOrderLabel: string;
    productDimensionsMatrixDisplayOrderSeparator: string;
}

/**
 * Data for the product dimensions matrix component.
 */
export interface IProductDimensionsMatrixData {
    dimensions: IProductDimensionsWithAvailabilitiesFull[];
    orderTemplates?: OrderTemplate[];
    customerInformation?: Customer;
}

/**
 * Props for the product dimensions matrix component.
 */
export interface IProductDimensionsMatrixProps {
    className?: string;
    context: ICoreContext<IDimensionsApp>;

    data: IProductDimensionsMatrixData;

    resources: IProductDimensionsMatrixResources;

    onErrorMessage(result: string): void;
    dimensionOnChange?(): void;
    isMultipleCatalogFeatureEnabledInHq: boolean;
    catalogId?: number | undefined;
}

/**
 * Represents a single node (cell data) in the matrix.
 */
export interface IProductDimensionsMatrixNode {
    dimensionIds: string[];

    isAvailable: boolean;

    quantity: number;

    productId?: number;
}

/**
 * State of the matrix.
 */
export interface IProductDimensionsMatrixState {
    dimensionsOrder: IProductDimensionsWithAvailabilitiesFull[];
    nodes: IProductDimensionsMatrixNode[];

    lastChangeAllDate: number;

    totalQuantity: number;

    dimensionsExpandState: Dictionary<string, boolean>;
    dimensionsExpandStateDate: number;
}

/**
 * Retrieves unique dimension id from the dimension data.
 * @param dimension - Dimension data.
 * @returns Unique id that represents the given dimension.
 */
const getDimensionId = (dimension: ProductDimensionValueInventoryAvailability) => {
    return `${dimension.DimensionTypeValue ?? ''}-${dimension.DimensionValue?.RecordId ?? ''}-${dimension.DimensionValue?.Value ?? ''}`;
};

/**
 * Retrieves dimension inventory level code.
 * @param dimensionId - Dimension id for which the code needs to be retrieved.
 * @param dimensions - List of the dimensions with the data.
 * @param inventoryLevel - Inventory level configuration.
 * @returns Inventory level code, for instance, available or out of stock.
 */
const retrieveDimensionInventoryLevelCode = (
    dimensionId: string,
    dimensions: ProductDimensionValueInventoryAvailability[],
    inventoryLevel?: InventoryLevels
) => {
    const dimension = dimensions.find(item => getDimensionId(item) === dimensionId)!;
    return getInventoryLevelCodeFromDimensionValue(dimension, inventoryLevel);
};

/**
 * Checks whether the given dimension is available for purchase or not.
 * @param dimensionId - Dimension id for which the availability needs to be retrieved.
 * @param dimensions - List of the dimensions with the data.
 * @param config - Inventory configuration.
 * @returns True if the dimension is available, false otherwise.
 */
export const isDimensionAvailable = (
    dimensionId: string,
    dimensions: ProductDimensionValueInventoryAvailability[],
    config: IDimensionsConfig
) => {
    const enableStockCheck = config.enableStockCheck;
    if (!enableStockCheck) {
        return true;
    }
    return retrieveDimensionInventoryLevelCode(dimensionId, dimensions, config.inventoryLevel) !== InventoryLevelValues.outOfStock;
};

/**
 * Checks whether the given product is available for purchase or not.
 * @param productAvailability - Product availability information.
 * @param inventoryLevel - Inventory level configuration.
 * @returns True if the product is available, false otherwise.
 */
const isProductAvailable = (productAvailability: ProductInventoryAvailability, inventoryLevel?: InventoryLevels) => {
    return getInventoryLevelCodeFromProductAvailability(productAvailability, inventoryLevel) !== InventoryLevelValues.outOfStock;
};

/**
 * Recursively builds matrix nodes based on the given dimensions data.
 * @param dimensionIds - Matrix of dimension ids for each dimension type.
 * @param dimensions - List of the dimensions with the data.
 * @param config - Inventory configuration.
 * @param defaultQuantity - Default quantity to use.
 * @returns List of created nodes.
 */
const retrieveEndDimensionsNode = (
    dimensionIds: string[][],
    dimensions: ProductDimensionValueInventoryAvailability[],
    config: IDimensionsConfig,
    defaultQuantity: number
): IProductDimensionsMatrixNode[] => {
    if (!ArrayExtensions.hasElements(dimensionIds)) {
        return [];
    }

    if (dimensionIds.length === 1) {
        return dimensionIds[0].map(dimensionId => {
            const isAvailable = isDimensionAvailable(dimensionId, dimensions, config);
            return {
                dimensionIds: [dimensionId],
                isAvailable,
                quantity: isAvailable ? defaultQuantity : 0
            };
        });
    }

    const nodes = retrieveEndDimensionsNode(dimensionIds.slice(1), dimensions, config, defaultQuantity);
    let endNodes: IProductDimensionsMatrixNode[] = [];
    for (const node of nodes) {
        endNodes = endNodes.concat(
            dimensionIds[0].map(dimension => {
                const isNodeAvailable = node.isAvailable && isDimensionAvailable(dimension, dimensions, config);
                const nodeCopy: IProductDimensionsMatrixNode = {
                    dimensionIds: node.dimensionIds.concat(dimension),
                    isAvailable: isNodeAvailable,
                    quantity: isNodeAvailable ? node.quantity : 0
                };

                return nodeCopy;
            })
        );
    }

    return endNodes;
};

/**
 * Generates a list of display orders.
 * @param dimensions - List of dimensions.
 * @param usedDimensions - List of used dimensions.
 * @returns List of display orders.
 */
const createDisplayOrders = (
    dimensions: ProductDimensionValueInventoryAvailability[],
    usedDimensions?: ProductDimensionValueInventoryAvailability[]
) => {
    if (usedDimensions?.length === dimensions.length) {
        return [
            {
                dimensionTypes: usedDimensions.map(dimension => dimension.DimensionTypeValue!)
            }
        ];
    }

    let result: {
        dimensionTypes: number[];
    }[] = [];

    for (const dimension of dimensions) {
        if (usedDimensions?.includes(dimension)) {
            continue;
        }
        const currentDimensions = usedDimensions ? usedDimensions.concat(dimension) : [dimension];
        const currentResult = createDisplayOrders(dimensions, currentDimensions);
        result = result.concat(currentResult);
    }

    return result;
};

/**
 * Represents dimension matrix row data for the react view.
 */
interface IProductDimensionsMatrixRow {
    rows: React.ReactElement[][];
    newNodeIndex: number;
}

/**
 * Product dimensions matrix component which allows to purchase multiple product variants at the same time.
 */
export class ProductDimensionsMatrix extends React.PureComponent<IProductDimensionsMatrixProps, IProductDimensionsMatrixState> {
    private static readonly _className: string = 'msc-product-dimensions-matrix';

    private static readonly _defaultQuantityProps = {
        defaultQuantity: 0,
        minQuantity: 0,
        maxQuantity: 10_000_000
    };

    private readonly _displayOrderList: {
        dimensionTypes: number[];
    }[];

    public constructor(props: IProductDimensionsMatrixProps) {
        super(props);

        const isMobileDevice = isMobile({ variant: VariantType.Viewport, context: this.props.context.request }) === 'xs';

        const dimensions = props.data.dimensions;
        const nodes = ProductDimensionsMatrix._retrieveNodes(isMobileDevice ? ArrayExtensions.reverse(dimensions) : dimensions, props);
        const quantities = nodes.map(node => node.quantity);
        this.state = {
            dimensionsOrder: dimensions,
            nodes,
            totalQuantity: ArrayExtensions.hasElements(quantities) ? ArrayExtensions.calculateSum(quantities) : 0,
            lastChangeAllDate: Date.now(),
            dimensionsExpandState: new Dictionary(),

            // The state needs to be updated in order to make collapsing work for inner dimensions.
            // Probably some react bug that makes the state not change which in turn, doesn't update expand state of the dimensions on the page.
            // Needs to be updated in order to update expand/collapse states and not to reset the whole component (which resets scroll position).
            // eslint-disable-next-line react/no-unused-state -- See the reason above.
            dimensionsExpandStateDate: Date.now()
        };

        this._displayOrderList = createDisplayOrders(dimensions);

        if (msdyn365Commerce.isBrowser) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises -- Call api to retrieve the availabilities data without blocking the UI.
            this.retrieveAvailabilitiesInformation();
        }
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    private static _retrieveNodes(dimensions: IProductDimensionsWithAvailabilitiesFull[], props: IProductDimensionsMatrixProps) {
        const dimensionIds = ArrayExtensions.validValues(
            dimensions.map(dimension => ArrayExtensions.validValues(dimension.dimensionValuesWithInventory?.map(getDimensionId)))
        );

        const dimensionsList = ArrayExtensions.flatten(dimensions.map(dimension => dimension.dimensionValuesWithInventory));

        const nodes = retrieveEndDimensionsNode(
            dimensionIds,
            dimensionsList,
            props.context.app.config,
            ProductDimensionsMatrix._defaultQuantityProps.defaultQuantity
        );

        for (const node of nodes) {
            const nodeDimensions = dimensionsList.filter(dimension => node.dimensionIds.includes(getDimensionId(dimension)));

            let productIds: number[] | undefined;
            for (const dimension of nodeDimensions) {
                const dimensionProductIds = dimension.ProductIds;
                if (!ArrayExtensions.hasElements(dimensionProductIds)) {
                    continue;
                }
                if (productIds === undefined) {
                    productIds = dimensionProductIds;
                    continue;
                }
                productIds = productIds.filter(productId => dimensionProductIds.includes(productId));
            }
            if (productIds?.length === 1) {
                node.productId = productIds[0];
            }
        }

        return nodes;
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    private static _renderMatrixEmptyCell(className: string): React.ReactElement {
        return <div key='empty-placeholder' className={className} />;
    }

    public render(): React.ReactElement | null {
        if (!ArrayExtensions.hasElements(this.state.nodes)) {
            return null;
        }

        return (
            <div className={classnames(this.props.className, ProductDimensionsMatrix._className)}>
                {this._renderHeader(`${ProductDimensionsMatrix._className}__header`)}
                {this._renderMatrix(`${ProductDimensionsMatrix._className}__matrix`)}
                {this._renderFooter(`${ProductDimensionsMatrix._className}__footer`)}
            </div>
        );
    }

    public async retrieveAvailabilitiesInformation() {
        const productIds = this.state.nodes.map(node => node.productId!);
        const actionContext = this.props.context.actionContext;
        const enableStockCheck = this.props.context.app.config.enableStockCheck;
        const warehouseAggregation = this.props.context.app.config.warehouseAggregation;

        if (!enableStockCheck) {
            for (const node of this.state.nodes) {
                node.isAvailable = true;
            }
        } else {
            const availabilitiesSearchCriteria: InventoryAvailabilitySearchCriteria = {
                ProductIds: productIds,
                QuantityUnitTypeValue: 2,
                FilterByChannelFulfillmentGroup: true,
                DeliveryModeTypeFilterValue: DeliveryMode.shipping,
                DefaultWarehouseOnly: warehouseAggregation === 'individual'
            };

            const availabilities = await ProductsDataActions.getEstimatedAvailabilityAsync(
                { callerContext: actionContext },
                availabilitiesSearchCriteria
            );

            for (const node of this.state.nodes) {
                const availability = availabilities.AggregatedProductInventoryAvailabilities?.find(
                    item => item.ProductId === node.productId
                );
                node.isAvailable =
                    availability !== undefined && isProductAvailable(availability, this.props.context.app.config.inventoryLevel);
            }
        }

        this.setState({
            lastChangeAllDate: Date.now()
        });
    }

    private _renderHeader(className: string): React.ReactElement {
        return (
            <div className={className}>
                {this._renderDisplayOrderContainer(`${className}__display-order`)}
                {this._renderActions(`${className}__actions`)}
            </div>
        );
    }

    /**
     * Handles change of dimension.
     * @param selectedDimensions - Selected dimension order.
     */
    private readonly _handleDisplayOrderChange = (selectedDimensions: ILabeledDropdownOnChangeNotification) => {
        this.setState(state => {
            const displayOrder = this._displayOrderList.find(value => {
                return value.dimensionTypes.join('-') === selectedDimensions.selectedOption.key;
            })!;
            const newDimensions = displayOrder.dimensionTypes.map(
                dimensionType => state.dimensionsOrder.find(item => item.DimensionTypeValue === dimensionType)!
            );

            const newNodes = this._generateNodesByNewDimensions(ArrayExtensions.reverse(newDimensions), state.nodes);

            return {
                dimensionsOrder: newDimensions,
                nodes: newNodes,
                lastChangeAllDate: Date.now()
            };
        });
    };

    private _renderDisplayOrderContainer(className: string) {
        const isMobileDevice = isMobile({ variant: VariantType.Viewport, context: this.props.context.request }) === 'xs';

        if (!isMobileDevice) {
            return null;
        }

        const dropdowns = this._displayOrderList.map(value => {
            return {
                key: value.dimensionTypes.join('-'),
                value: value.dimensionTypes
                    .map(dimension => getDimensionName(dimension, this.props.resources))
                    .join(this.props.resources.productDimensionsMatrixDisplayOrderSeparator)
            };
        });

        return (
            <div className={className}>
                <LabeledDropdown
                    labelClassname={`${className}__label`}
                    labelText={this.props.resources.productDimensionsMatrixDisplayOrderLabel}
                    dropdownOptions={dropdowns}
                    selectedOption={dropdowns[0]}
                    dropdownClassname={`${className}__dropdown`}
                    onSelectOption={this._handleDisplayOrderChange}
                />
            </div>
        );
    }

    /**
     * Updates all quantities for each cell with the new given quantity, overrides any existing quantities.
     * @param newQuantity - Quantity to use for the cells.
     */
    private readonly _updateAllQuantities = (newQuantity: number) => {
        this.setState(state => {
            const nodes = state.nodes.map(node => {
                return {
                    ...node,
                    quantity: node.isAvailable ? newQuantity : 0
                };
            });
            const quantities = nodes.map(node => node.quantity);
            return {
                nodes,
                totalQuantity: ArrayExtensions.hasElements(quantities) ? ArrayExtensions.calculateSum(quantities) : 0,
                lastChangeAllDate: Date.now()
            };
        });
    };

    /**
     * Clears all quantities by setting all nodes to use the default quantity.
     */
    private readonly _clearAllQuantities = () => {
        this._updateAllQuantities(ProductDimensionsMatrix._defaultQuantityProps.defaultQuantity);
    };

    private _renderActions(className: string): React.ReactElement {
        return (
            <div className={className}>
                {this._renderEnterAllQuantities(`${className}__enter-all-quantities-container`)}
                {this._renderClearAllQuantities(`${className}__clear-all-quantities-container`)}
            </div>
        );
    }

    private _renderEnterAllQuantities(className: string): React.ReactElement {
        const maxQuantityForCartLineItem = this.props.context.app.config.maxQuantityForCartLineItem;
        return (
            <ProductDimensionsMatrixEnterAllQuantitiesComponent
                className={className}
                resources={this.props.resources}
                defaultQuantity={ProductDimensionsMatrix._defaultQuantityProps.defaultQuantity}
                minQuantityValue={ProductDimensionsMatrix._defaultQuantityProps.minQuantity}
                maxQuantityValue={maxQuantityForCartLineItem || ProductDimensionsMatrix._defaultQuantityProps.maxQuantity}
                onApplyToAll={this._updateAllQuantities}
            />
        );
    }

    private _renderClearAllQuantities(className: string): React.ReactElement {
        return (
            <div className={className}>
                <Button
                    className={`${className}__button`}
                    title={this.props.resources.productDimensionsMatrixClearAllText}
                    onClick={this._clearAllQuantities}
                >
                    {this.props.resources.productDimensionsMatrixClearAllText}
                </Button>
            </div>
        );
    }

    private _renderMatrixDesktop(className: string): React.ReactElement {
        const rowClassName = `${className}__row`;
        const cellClassName = `${rowClassName}__cell`;
        const dimensionsList = ArrayExtensions.flatten(this.state.dimensionsOrder.map(dimension => dimension.dimensionValuesWithInventory));
        const rows = this._renderMatrixRowsDesktop(rowClassName, dimensionsList, [], 0).rows;

        const reversedDimensions = ArrayExtensions.reverse(this.state.dimensionsOrder);

        return (
            <div className={className} key={JSON.stringify(this.state.dimensionsOrder)}>
                {this._renderMatrixDimensionsHeader(`${className}__dimensions-header`, reversedDimensions)}
                <div className={`${className}__content`}>
                    <div className={rowClassName}>
                        {reversedDimensions.map((dimension, index) => {
                            if (index === reversedDimensions.length - 1) {
                                return dimension.dimensionValuesWithInventory?.map(item =>
                                    this._renderMatrixDimensionCell(cellClassName, item)
                                );
                            }
                            return ProductDimensionsMatrix._renderMatrixEmptyCell(cellClassName);
                        })}
                    </div>
                    {rows.map(cells => {
                        return (
                            <div className={rowClassName} key='cells'>
                                {cells}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    /**
     * Handle collapse dimension.
     * @param dimensionId - Selected dimension id.
     * @returns Callback for collapse dimension button.
     */
    private readonly _handleCollapseDimension = (dimensionId: string) => () => {
        this.setState(state => {
            const isExpanded = state.dimensionsExpandState.getValueWithDefaultValue(dimensionId, false);
            state.dimensionsExpandState.setValue(dimensionId, !isExpanded);

            return {
                dimensionsExpandState: state.dimensionsExpandState,
                dimensionsExpandStateDate: Date.now()
            };
        });
    };

    private _renderMatrixMobile(className: string): React.ReactElement {
        const allDimensions = ArrayExtensions.flatten(this.state.dimensionsOrder.map(dimension => dimension.dimensionValuesWithInventory));

        const singleDimensionClassName = `${className}__single-dimension`;
        const cellClassName = `${className}__cell`;
        const dimensionsContainerClassName = `${cellClassName}__dimension-container`;

        return (
            <div
                className={classnames(className, this.state.dimensionsOrder.length === 1 ? singleDimensionClassName : '')}
                key={`${JSON.stringify(this.state.dimensionsOrder)}-${this.state.lastChangeAllDate}`}
            >
                {this._renderMatrixRowsMobile(dimensionsContainerClassName, allDimensions, [], 0).rows}
            </div>
        );
    }

    private _renderMatrix(className: string): React.ReactElement {
        const isMobileDevice = isMobile({ variant: VariantType.Viewport, context: this.props.context.request }) === 'xs';

        if (!isMobileDevice) {
            return this._renderMatrixDesktop(className);
        }

        return this._renderMatrixMobile(`${className}__mobile`);
    }

    private _generateNodesByNewDimensions(
        newDimensions: IProductDimensionsWithAvailabilitiesFull[],
        nodes: IProductDimensionsMatrixNode[]
    ) {
        return ProductDimensionsMatrix._retrieveNodes(newDimensions, this.props).map(node => {
            const previousNode = nodes.find(pastNode => ArrayExtensions.haveSameItems(node.dimensionIds, pastNode.dimensionIds))!;
            node.quantity = previousNode.quantity;
            node.isAvailable = previousNode.isAvailable;
            return node;
        });
    }

    /**
     * Handles change of dimension for a column by swapping previous and new dimension columns with each other.
     * @param previousDimensionType - Previous dimension used in the column.
     * @param newDimensionType - New dimension to display instead of the previous dimension.
     */
    private readonly _handleDimensionTypeChange = (previousDimensionType: number, newDimensionType: number) => {
        this.setState(
            state => {
                const previousDimension = state.dimensionsOrder.find(item => item.DimensionTypeValue === previousDimensionType)!;
                const newDimension = state.dimensionsOrder.find(item => item.DimensionTypeValue === newDimensionType)!;

                const newDimensions = state.dimensionsOrder.map(item => {
                    if (item === previousDimension) {
                        return newDimension;
                    }
                    if (item === newDimension) {
                        return previousDimension;
                    }
                    return item;
                });

                const newNodes = this._generateNodesByNewDimensions(newDimensions, state.nodes);

                return {
                    dimensionsOrder: newDimensions,
                    nodes: newNodes,
                    lastChangeAllDate: Date.now()
                };
            },
            () => {
                this.props.dimensionOnChange && this.props.dimensionOnChange();
            }
        );
    };

    private _renderMatrixDimensionsHeader(className: string, dimensions: IProductDimensionsWithAvailabilitiesFull[]): React.ReactElement {
        const cellClassName = `${className}__cell`;
        const dimensionsList = dimensions.map(dimension => dimension.DimensionTypeValue);

        return (
            <div className={className}>
                {dimensionsList.map(dimensionType => {
                    return (
                        <ProductDimensionsMatrixDimensionSelectionComponent
                            key={dimensionType}
                            className={cellClassName}
                            resources={this.props.resources}
                            dimensionTypes={dimensionsList}
                            currentDimensionType={dimensionType}
                            onDimensionTypeChange={this._handleDimensionTypeChange}
                        />
                    );
                })}
            </div>
        );
    }

    private _renderMatrixMainDimensionMobile(
        className: string,
        dimension: ProductDimensionValueInventoryAvailability,
        content: React.ReactElement
    ): React.ReactElement {
        const dimensionId = getDimensionId(dimension);
        const isExpanded = this.state.dimensionsExpandState.getValueWithDefaultValue(dimensionId, false);

        const expandButtonClassName = `${className}__expand-button`;
        const collapseClassName = `${className}__collapse`;
        const collapseItemClassName = `${collapseClassName}__item`;

        return (
            <div className={className} key={`${dimensionId}-${isExpanded ? 'expanded' : 'collapsed'}-${this.state.lastChangeAllDate}`}>
                <Button className={expandButtonClassName} onClick={this._handleCollapseDimension(dimensionId)} aria-expanded={isExpanded}>
                    {this._renderMatrixDimensionCell(`${expandButtonClassName}__dimension`, dimension)}
                </Button>
                <Collapse className={collapseClassName} isOpen={isExpanded}>
                    <div className={collapseItemClassName}>{content}</div>
                </Collapse>
            </div>
        );
    }

    private _renderMatrixRowsMobile(
        className: string,
        dimensions: ProductDimensionValueInventoryAvailability[],
        partialDimensions: string[],
        nodeIndex: number
    ): IProductDimensionsMatrixRow {
        const result: IProductDimensionsMatrixRow = {
            rows: [],
            newNodeIndex: nodeIndex
        };

        if (nodeIndex >= this.state.nodes.length) {
            return result;
        }

        const node = this.state.nodes[result.newNodeIndex];
        const nodeDimensionIdsLength = node.dimensionIds.length;
        const partialDimensionIdsLength = partialDimensions.length;
        const currentDimensionId = node.dimensionIds[partialDimensionIdsLength];
        const currentDimension = dimensions.find(dimension => getDimensionId(dimension) === currentDimensionId)!;

        const cellClassName = `${className}__cell`;

        if (nodeDimensionIdsLength === partialDimensionIdsLength + 1) {
            result.rows.push([]);

            if (node.dimensionIds.length === 1) {
                while (result.newNodeIndex < this.state.nodes.length) {
                    const dimensionId = this.state.nodes[result.newNodeIndex].dimensionIds[partialDimensionIdsLength];
                    const lastDimension = dimensions.find(dimension => getDimensionId(dimension) === dimensionId)!;

                    result.rows[0].push(
                        <div className={cellClassName}>
                            {this._renderMatrixDimensionCell(`${cellClassName}__dimension-label`, lastDimension)}
                            {this._renderMatrixCell(`${cellClassName}__content`, result.newNodeIndex++)}
                        </div>
                    );
                }
                return result;
            }

            result.rows[0].push(
                <div className={cellClassName}>
                    {this._renderMatrixDimensionCell(`${cellClassName}__dimension-label`, currentDimension)}
                    {this._renderMatrixCell(`${cellClassName}__content`, result.newNodeIndex++)}
                </div>
            );

            if (partialDimensionIdsLength > 0) {
                const previousDimension = partialDimensions[partialDimensionIdsLength - 1];

                while (
                    result.newNodeIndex < this.state.nodes.length &&
                    previousDimension === this.state.nodes[result.newNodeIndex].dimensionIds[partialDimensionIdsLength - 1]
                ) {
                    const dimensionId = this.state.nodes[result.newNodeIndex].dimensionIds[partialDimensionIdsLength];
                    const lastDimension = dimensions.find(dimension => getDimensionId(dimension) === dimensionId)!;
                    result.rows[0].push(
                        <div className={cellClassName}>
                            {this._renderMatrixDimensionCell(`${cellClassName}__dimension-label`, lastDimension)}
                            {this._renderMatrixCell(`${cellClassName}__content`, result.newNodeIndex++)}
                        </div>
                    );
                }
            }

            return result;
        }

        const rows = this._renderMatrixRowsMobile(className, dimensions, partialDimensions.concat(currentDimensionId), result.newNodeIndex);
        result.newNodeIndex = rows.newNodeIndex;
        if (partialDimensionIdsLength === 0) {
            result.rows.push([this._renderMatrixMainDimensionMobile(`${className}__main-dimension`, currentDimension, <>{rows.rows}</>)]);
        } else {
            result.rows.push([
                <div className={`${className}__container`} key={currentDimensionId}>
                    {this._renderMatrixDimensionCell(`${className}__container__dimension`, currentDimension)}
                    <div className={`${className}__container__items`}>{rows.rows}</div>
                </div>
            ]);
        }

        if (partialDimensionIdsLength > 0) {
            const previousDimension = partialDimensions[partialDimensionIdsLength - 1];
            while (
                result.newNodeIndex < this.state.nodes.length &&
                previousDimension === this.state.nodes[result.newNodeIndex].dimensionIds[partialDimensionIdsLength - 1]
            ) {
                const nextRows = this._renderMatrixRowsMobile(className, dimensions, partialDimensions, result.newNodeIndex);
                result.rows = result.rows.concat(nextRows.rows);
                result.newNodeIndex = nextRows.newNodeIndex;
            }
        } else {
            while (result.newNodeIndex < this.state.nodes.length) {
                const nextRows = this._renderMatrixRowsMobile(className, dimensions, partialDimensions, result.newNodeIndex);
                result.rows = result.rows.concat(nextRows.rows);
                result.newNodeIndex = nextRows.newNodeIndex;
            }
        }

        return result;
    }

    private _renderMatrixRowsDesktop(
        className: string,
        dimensions: ProductDimensionValueInventoryAvailability[],
        partialDimensions: string[],
        nodeIndex: number
    ): IProductDimensionsMatrixRow {
        const result: IProductDimensionsMatrixRow = {
            rows: [],
            newNodeIndex: nodeIndex
        };

        if (nodeIndex >= this.state.nodes.length) {
            return result;
        }

        const node = this.state.nodes[result.newNodeIndex];
        const nodeDimensionIdsLength = node.dimensionIds.length;
        const partialDimensionIdsLength = partialDimensions.length;
        const currentDimensionId = node.dimensionIds[partialDimensionIdsLength];
        const currentDimension = dimensions.find(dimension => getDimensionId(dimension) === currentDimensionId)!;

        const cellClassName = `${className}__cell`;

        if (nodeDimensionIdsLength === partialDimensionIdsLength + 1) {
            result.rows.push([]);

            if (node.dimensionIds.length === 1) {
                while (result.newNodeIndex < this.state.nodes.length) {
                    result.rows[0].push(this._renderMatrixCell(cellClassName, result.newNodeIndex++));
                }
                return result;
            }

            result.rows[0].push(this._renderMatrixCell(cellClassName, result.newNodeIndex++));

            if (partialDimensionIdsLength > 0) {
                const previousDimension = partialDimensions[partialDimensionIdsLength - 1];

                while (
                    result.newNodeIndex < this.state.nodes.length &&
                    previousDimension === this.state.nodes[result.newNodeIndex].dimensionIds[partialDimensionIdsLength - 1]
                ) {
                    result.rows[0].push(this._renderMatrixCell(cellClassName, result.newNodeIndex++));
                }
            }

            return result;
        }

        const rows = this._renderMatrixRowsDesktop(
            className,
            dimensions,
            partialDimensions.concat(currentDimensionId),
            result.newNodeIndex
        );
        result.rows = result.rows.concat(
            rows.rows.map((cells, index) => {
                if (index === 0) {
                    return [this._renderMatrixDimensionCell(cellClassName, currentDimension)].concat(cells);
                }
                return [ProductDimensionsMatrix._renderMatrixEmptyCell(cellClassName)].concat(cells);
            })
        );
        result.newNodeIndex = rows.newNodeIndex;

        if (partialDimensionIdsLength > 0) {
            const previousDimension = partialDimensions[partialDimensionIdsLength - 1];
            while (
                result.newNodeIndex < this.state.nodes.length &&
                previousDimension === this.state.nodes[result.newNodeIndex].dimensionIds[partialDimensionIdsLength - 1]
            ) {
                const nextRows = this._renderMatrixRowsDesktop(className, dimensions, partialDimensions, result.newNodeIndex);
                result.rows = result.rows.concat(nextRows.rows);
                result.newNodeIndex = nextRows.newNodeIndex;
            }
        } else {
            while (result.newNodeIndex < this.state.nodes.length) {
                const nextRows = this._renderMatrixRowsDesktop(className, dimensions, partialDimensions, result.newNodeIndex);
                result.rows = result.rows.concat(nextRows.rows);
                result.newNodeIndex = nextRows.newNodeIndex;
            }
        }

        return result;
    }

    private _renderMatrixDimensionCell(className: string, dimension: ProductDimensionValueInventoryAvailability): React.ReactElement {
        const dimensionId = getDimensionId(dimension);

        return (
            <div className={className} key={dimensionId}>
                <ProductDimensionsMatrixDimensionItemComponent
                    className={`${className}__dimension-item`}
                    apiSettings={this.props.context.request.apiSettings}
                    item={{
                        itemId: dimensionId,
                        value: dimension.DimensionValue?.Value ?? '',
                        colorHexCode: dimension.DimensionValue?.ColorHexCode,
                        imageUrl: dimension.DimensionValue?.ImageUrl
                    }}
                />
            </div>
        );
    }

    private _renderMatrixCell(className: string, nodeIndex: number): React.ReactElement {
        const node = this.state.nodes[nodeIndex];

        return (
            <div
                key={`${JSON.stringify(node.dimensionIds)}-${this.state.lastChangeAllDate}`}
                className={classnames(className, node.isAvailable ? `${className}__available` : `${className}__unavailable`)}
            >
                {node.isAvailable ? this._renderMatrixQuantityCell(node) : this._renderMatrixOutOfStockCell(`${className}__out-of-stock`)}
            </div>
        );
    }

    private _renderMatrixOutOfStockCell(className: string): React.ReactElement {
        return (
            <div className={className} key='out-of-stock'>
                {this.props.resources.productDimensionsMatrixOutOfStockLabel}
            </div>
        );
    }

    private _renderMatrixQuantityCell(node: IProductDimensionsMatrixNode): React.ReactElement {
        const maxQuantityForCartLineItem = this.props.context.app.config.maxQuantityForCartLineItem;
        return (
            <Quantity
                key={`${JSON.stringify(node.dimensionIds)}-${this.state.lastChangeAllDate}`}
                currentCount={node.quantity}
                onChange={this._handleQuantityChange(node)}
                inputQuantityAriaLabel={this.props.resources.productDimensionsMatrixInputQuantityAriaLabel}
                min={ProductDimensionsMatrix._defaultQuantityProps.minQuantity}
                max={maxQuantityForCartLineItem || ProductDimensionsMatrix._defaultQuantityProps.maxQuantity}
            />
        );
    }

    private _renderTotalSummary(className: string): React.ReactElement {
        return (
            <div className={className}>
                <div className={`${className}__total-quantity`}>{this.state.totalQuantity}</div>
                <div className={`${className}__total-quantity-label`}>{this.props.resources.productDimensionsMatrixTotalItemsLabel}</div>
            </div>
        );
    }

    private _retrieveProducts(): { productId: number; quantity: number }[] {
        return this.state.nodes
            .filter(node => node.productId && node.quantity !== 0)
            .map(node => {
                return {
                    productId: node.productId!,
                    quantity: node.quantity
                };
            });
    }

    private _renderAddToOrderTemplate(className: string): React.ReactElement | null {
        if (ObjectExtensions.isNullOrUndefined(this.props.data.orderTemplates)) {
            return null;
        }

        return (
            <AddProductsToOrderTemplateComponent
                className={className}
                context={this.props.context}
                data={{
                    orderTemplates: this.props.data.orderTemplates,
                    products: this._retrieveProducts().map(item => {
                        return {
                            ProductId: item.productId,
                            Quantity: item.quantity,
                            UnitOfMeasure: ''
                        };
                    }),
                    customerInformation: this.props.data.customerInformation
                }}
                resources={this.props.resources}
                isDisabled={this.state.totalQuantity === 0}
                isMultipleCatalogFeatureEnabledInHq={this.props.isMultipleCatalogFeatureEnabledInHq}
                catalogId={this.props.catalogId}
            />
        );
    }

    private _renderAddToCart(className: string): React.ReactElement {
        return (
            <AddToCartFunctionalComponent
                key={`${JSON.stringify(this.state.nodes)}`}
                addToCartText={this.props.resources.productDimensionsMatrixAddItemsToCartButtonText}
                addToCartArialLabel={this.props.resources.productDimensionsMatrixAddItemsToCartButtonText}
                id=''
                typeName=''
                className={className}
                navigationUrl={getUrlSync('cart', this.props.context.actionContext)}
                context={this.props.context}
                dialogStrings={this.props.resources}
                shouldSkipSiteSettings
                data={undefined}
                products={this._retrieveProducts()}
                disabled={this.state.totalQuantity === 0}
                onError={(result): void => {
                    if (result.cartActionResult?.errorDetails?.LocalizedMessage) {
                        this.props.onErrorMessage(result.cartActionResult.errorDetails.LocalizedMessage);
                    }
                }}
            />
        );
    }

    private _renderFooter(className: string): React.ReactElement {
        return (
            <div className={className}>
                {this._renderTotalSummary(`${className}__total-summary`)}
                {this._renderFooterActions(`${className}__actions`)}
            </div>
        );
    }

    private _renderFooterActions(className: string): React.ReactElement {
        return (
            <div className={className}>
                {this._renderAddToOrderTemplate(`${className}__add-to-order-template`)}
                {this._renderAddToCart(`${className}__add-to-cart`)}
            </div>
        );
    }

    /**
     * Handles quantity change.
     * @param node - Node which needs to be updated.
     * @returns Handler method.
     */
    private readonly _handleQuantityChange = (node: IProductDimensionsMatrixNode) => (newValue: number = 0) => {
        if (!node.isAvailable) {
            return;
        }

        const difference = newValue - node.quantity;
        node.quantity = newValue;
        this.setState(state => {
            return {
                totalQuantity: state.totalQuantity + difference
            };
        });
    };
}
